import { Html } from '@react-three/drei'
import type { VehicleSceneTheme } from './VehicleSceneTheme'

const M = (mm: number) => mm / 1000

const TIRE_GRAPHITE = '#3f464f'
const RIM_LIGHT_STEEL = '#c4ccd6'

const AXLE_COLORS: Record<string, string> = {
  steering: '#3b82f6',
  drive: '#10b981',
  trailer: '#f59e0b',
  tag: '#f59e0b',
  other: '#f59e0b',
}

const AXLE_LABELS: Record<string, string> = {
  steering: 'Dir.',
  drive: 'Mot.',
  trailer: 'Rem.',
  tag: 'Tag',
  other: 'Aut.',
}

function VehicleWheel({
  axleX, wheelY, radius, color,
}: {
  axleX: number; wheelY: number; radius: number; color: string
}) {
  const tireRadius = M(radius)
  const rimRadius = tireRadius * 0.56
  const hubRadius = tireRadius * 0.2
  const tireWidth = tireRadius * 0.62

  return (
    <group position={[M(axleX), -tireRadius, M(wheelY)]} rotation={[Math.PI / 2, 0, 0]}>
      <mesh castShadow receiveShadow>
        <cylinderGeometry args={[tireRadius, tireRadius, tireWidth, 28]} />
        <meshStandardMaterial color={TIRE_GRAPHITE} roughness={0.92} metalness={0.05} />
      </mesh>
      <mesh position={[0, tireWidth * 0.02, 0]}>
        <cylinderGeometry args={[rimRadius, rimRadius, tireWidth * 1.02, 20]} />
        <meshStandardMaterial color={RIM_LIGHT_STEEL} metalness={0.85} roughness={0.25} />
      </mesh>
      {[1, -1].map(side => (
        <mesh key={side} position={[0, side * tireWidth * 0.52, 0]}>
          <cylinderGeometry args={[hubRadius, hubRadius, tireWidth * 0.06, 16]} />
          <meshStandardMaterial color={color} metalness={0.6} roughness={0.3} emissive={color} emissiveIntensity={0.25} />
        </mesh>
      ))}
    </group>
  )
}

export function VehicleAxleGroup({
  axle, index, width, trackWidth, showLabels,
}: {
  axle: any; index: number; width: number; trackWidth?: number; showLabels: boolean; theme: VehicleSceneTheme
}) {
  const position = Number(axle.position ?? 0)
  const radius = 450
  const tireWidth = radius * 0.62
  const effectiveTrackWidth = typeof trackWidth === 'number' && Number.isFinite(trackWidth) && trackWidth > 0
    ? trackWidth
    : undefined
  const wheelCenters = effectiveTrackWidth !== undefined
    ? [width / 2 - effectiveTrackWidth / 2, width / 2 + effectiveTrackWidth / 2]
    : [tireWidth / 2, width - tireWidth / 2]
  const axleSpan = wheelCenters[1] - wheelCenters[0]
  const color = AXLE_COLORS[axle.type as string] ?? '#f59e0b'
  const labelType = AXLE_LABELS[axle.type as string] ?? axle.type ?? '?'
  const label = `${axle.id ?? `E${index + 1}`} · ${labelType}`

  return (
    <group>
      <VehicleWheel axleX={position} wheelY={wheelCenters[0]} radius={radius} color={color} />
      <VehicleWheel axleX={position} wheelY={wheelCenters[1]} radius={radius} color={color} />
      <mesh position={[M(position), -M(radius), M(width / 2)]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[M(70), M(70), M(axleSpan), 12]} />
        <meshStandardMaterial color={color} metalness={0.7} roughness={0.35} />
      </mesh>
      {showLabels && (
        <Html position={[M(position), M(radius * 0.15), M(width / 2)]} center distanceFactor={8} occlude={false}>
          <div className="whitespace-nowrap rounded-full px-2 py-0.5 text-[9px] font-bold font-mono shadow-md" style={{ backgroundColor: color, color: '#fff' }}>
            {label}
          </div>
        </Html>
      )}
    </group>
  )
}
